const BASE_URL = 'https://openrouter.ai/api/v1';
/** Initial-connect retry once on transient OpenRouter failures.
 * Only applies to the pre-stream POST — once SSE chunks arrive we
 * can't retry without losing partial output. Mirrors the Convex
 * action retry in `convex/nipeChat.ts`. */
const RETRYABLE_STATUSES = new Set([429, 500, 502, 503, 504]);
const RETRY_BACKOFF_MS = 400;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StreamCompletionConfig {
  model: string;
  messages: Array<{ role: string; content: string }>;
  apiKey: string;
  onChunk: (chunk: string) => void;
  onComplete: (fullText: string) => void;
  onError: (error: Error) => void;
  signal?: AbortSignal;
}

export interface OpenRouterModel {
  id: string;
  name: string;
  context_length: number;
  pricing: { prompt: string; completion: string };
}

// ---------------------------------------------------------------------------
// Streaming completion
// ---------------------------------------------------------------------------

export async function streamCompletion(config: StreamCompletionConfig): Promise<void> {
  const { model, messages, apiKey, onChunk, onComplete, onError, signal } = config;

  async function connectOnce(): Promise<
    | { kind: 'ok'; response: Response }
    | { kind: 'retryable' | 'fatal'; error: Error }
    | { kind: 'aborted' }
  > {
    try {
      const response = await fetch(`${BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': typeof window !== 'undefined' ? window.location.origin : '',
          'X-Title': 'Repancy',
        },
        body: JSON.stringify({ model, messages, stream: true }),
        signal,
      });
      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        const error = new Error(`OpenRouter ${response.status}: ${text}`);
        return {
          kind: RETRYABLE_STATUSES.has(response.status) ? ('retryable' as const) : ('fatal' as const),
          error,
        };
      }
      return { kind: 'ok' as const, response };
    } catch (err) {
      if (signal?.aborted) return { kind: 'aborted' as const };
      return {
        kind: 'retryable' as const,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }

  let response: Response;
  const first = await connectOnce();
  if (first.kind === 'aborted') return;
  if (first.kind === 'ok') {
    response = first.response;
  } else if (first.kind === 'fatal') {
    onError(first.error);
    return;
  } else {
    // Retryable: brief backoff, single retry. Once SSE chunks flow we
    // can't safely retry mid-stream — only the initial connect.
    console.warn(`[openrouter] transient failure, retrying once: ${first.error.message}`);
    await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF_MS));
    const second = await connectOnce();
    if (second.kind === 'aborted') return;
    if (second.kind === 'ok') {
      response = second.response;
    } else {
      onError(new Error(`${second.error.message} (after retry)`));
      return;
    }
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError(new Error('Response body is not readable'));
    return;
  }

  const decoder = new TextDecoder();
  let fullText = '';
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE lines
      const lines = buffer.split('\n');
      // Keep the last potentially incomplete line in the buffer
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':')) continue;

        if (!trimmed.startsWith('data: ')) continue;
        const payload = trimmed.slice(6);

        if (payload === '[DONE]') {
          onComplete(fullText);
          return;
        }

        try {
          const parsed = JSON.parse(payload);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (typeof delta === 'string' && delta.length > 0) {
            fullText += delta;
            onChunk(delta);
          }
        } catch {
          // Skip malformed JSON chunks
        }
      }
    }

    // Stream ended without [DONE] sentinel
    onComplete(fullText);
  } catch (err) {
    if (signal?.aborted) return;
    onError(err instanceof Error ? err : new Error(String(err)));
  }
}

// ---------------------------------------------------------------------------
// Fetch available models
// ---------------------------------------------------------------------------

export async function fetchModels(apiKey: string): Promise<OpenRouterModel[]> {
  const response = await fetch(`${BASE_URL}/models`, {
    headers: {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`OpenRouter ${response.status}: ${response.statusText}`);
  }

  const json = await response.json();
  return (json.data ?? []) as OpenRouterModel[];
}

// ---------------------------------------------------------------------------
// Prompt hash for cache key
// ---------------------------------------------------------------------------

export async function computePromptHash(
  systemPrompt: string,
  userPrompt: string,
  skillPrompts: string[],
  modelId: string,
  docHashes: string[],
): Promise<string> {
  const input = JSON.stringify({ systemPrompt, userPrompt, skillPrompts, modelId, docHashes });
  const encoded = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
